// Sector landing pages — hyper-specific service × city combinations targeting
// long-tail search intent. Each entry is curated (not auto-generated) and
// linked from both the parent service and city pages via internal SEO.

import { serviceBySlug } from "./services";
import { cityBySlug } from "./cities";

const raw = [
  {
    slug: "mining-security-thompson",
    serviceSlug: "mining-security",
    citySlug: "thompson",
    heading: "Mining Security. Thompson, Manitoba.",
    lede:
      "Nickel-belt security programs built for the pace of a 24/7 industrial operation in northern Manitoba's largest resource community.",
    body: [
      "Thompson's economy runs on nickel. The mill, the refinery, the surrounding satellite operations, the fleet yards, the fuel depots, the contractor camps — every one of them requires a security program that understands the mining environment and the geography that shapes it. Manitoba Security Services builds programs for the Thompson corridor that align with each operator's site-specific access matrix, hazard zones, and shift patterns.",
      "Our Thompson gate-control postings verify credentials, PPE compliance, contractor status, and inbound freight against a shift-by-shift log. Roving patrols cover haul-road choke points, laydown yards, fuel storage, and the perimeter fence line on GPS-verified randomised routes. Every entry, exit, and anomaly is documented and delivered to the client dispatch within the operating cycle.",
      "The community context matters as much as the technical program. Officers are trained on cultural awareness for work adjacent to Nisichawayasihk Cree Nation and other neighbouring communities, verbal de-escalation for contractor-camp environments, and coordination with the Thompson RCMP detachment for evidence handling. The result is a security posture that protects the asset and preserves the operator's community licence to operate."
    ],
    keywords: [
      "mining security Thompson",
      "nickel mine security Manitoba",
      "Thompson site security guards",
      "remote site security Thompson",
      "24/7 mine security Manitoba"
    ]
  },
  {
    slug: "mining-security-flin-flon",
    serviceSlug: "mining-security",
    citySlug: "flin-flon",
    heading: "Mining Security. Flin Flon, Manitoba.",
    lede:
      "Copper-zinc security programs built for a community whose identity is inseparable from the operations we protect.",
    body: [
      "Flin Flon is a mining town in the truest sense. The concentrator, the smelter footprint, the surrounding tailings and infrastructure, and the contractor traffic that sustains all of it produce a security environment where mistakes are expensive and improvisation is unacceptable. Manitoba Security Services staffs Flin Flon operations with mining-experienced officers who understand the operational language and the pace of the site.",
      "Gate control, credential integrity, contractor management, roving patrols, and emergency response coordination form the core of every Flin Flon posting. We integrate with the operator's existing access-control systems, escalate anomalies to control-room dispatch, and hold current WHMIS and site-specific safety certifications where the client requires. The Flin Flon RCMP detachment is our primary law-enforcement liaison for evidence chain-of-custody handling and incident escalation.",
      "For exploration camps and satellite operations extending toward Snow Lake and Cranberry Portage, we scale to a scheduled mobile posture with roving patrol coverage aligned to the client's operational cycles."
    ],
    keywords: [
      "mining security Flin Flon",
      "copper zinc mine security",
      "Flin Flon site security",
      "concentrator security Manitoba",
      "smelter security Flin Flon"
    ]
  },
  {
    slug: "mining-security-snow-lake",
    serviceSlug: "mining-security",
    citySlug: "snow-lake",
    heading: "Mining Security. Snow Lake, Manitoba.",
    lede:
      "Gold-belt security programs for active production sites and exploration camps across northern Manitoba's most productive mineral corridor.",
    body: [
      "Snow Lake sits in the heart of Manitoba's active gold and base-metal country. Manitoba Security Services covers production sites, exploration camps, and contractor accommodations across the Snow Lake footprint with mining-experienced officers who understand the specific security profile of a gold operation — bullion movement, magazine security, high-value ore inventory, and the personnel-safety demands of a fly-in camp.",
      "Our Snow Lake postings integrate with the operator's control-room dispatch, the site emergency response plan, and — where the mine holds explosives on site — the regulatory access controls that govern magazine approach. Roving patrols cover gate approaches, fuel depots, mill infrastructure, and the perimeter on randomised GPS-verified schedules.",
      "For clients running exploration camps in the surrounding claims, we deploy on scheduled rotations with cross-trained personnel who cover both physical security and personnel-safety functions."
    ],
    keywords: [
      "mining security Snow Lake",
      "gold mine security Manitoba",
      "exploration camp security",
      "Snow Lake site security"
    ]
  },
  {
    slug: "oil-gas-security-melita",
    serviceSlug: "oil-gas-site-security",
    citySlug: "melita",
    heading: "Oil & Gas Security. Melita, Manitoba.",
    lede:
      "Wellhead, gathering-facility, and pipeline right-of-way security across the most active pocket of the Manitoba Bakken.",
    body: [
      "Melita anchors Manitoba's most active oil and gas play. Wellheads, pump stations, gathering facilities, tank batteries, and the pipeline right-of-ways that connect them are exposed to copper theft, hydraulic fluid siphoning, unauthorised entry, and occasional protest activity. Manitoba Security Services provides mobile patrol coverage across the Melita operator footprint on schedules aligned with the client's operational cycles.",
      "Right-of-way patrols confirm the integrity of pipeline corridors, document ground conditions after weather events, and identify unauthorised excavation activity near buried assets. Wellhead and pump-station checks are logged against GIS-defined checkpoint layers and delivered to control-room dispatch as inspection-grade reports.",
      "For operator emergencies — spill response staging, evacuation coordination, media perimeter control — our senior officers integrate with the client's incident command structure and the local RCMP detachment."
    ],
    keywords: [
      "oil gas security Melita",
      "wellhead security Manitoba",
      "pipeline patrol Melita",
      "Manitoba Bakken security"
    ]
  },
  {
    slug: "oil-gas-security-virden",
    serviceSlug: "oil-gas-site-security",
    citySlug: "virden",
    heading: "Oil & Gas Security. Virden, Manitoba.",
    lede:
      "Coverage for Manitoba's second oil-country hub — from wellhead protection to community-facing operations.",
    body: [
      "Virden's oil economy dates back generations. Today's Virden operator footprint spans active wells, workover activity, gathering systems, and administrative offices that require year-round coverage. Manitoba Security Services provides Virden operators with mobile patrol, static coverage during turnaround events, and community-adjacent security for corporate offices along Highway 1.",
      "Our Virden patrols run randomised GPS-verified schedules across the operator's asset footprint, with priority coverage of wellhead approaches, tank batteries, and pipeline exposure points during elevated-risk windows. Every checkpoint is logged, every anomaly is escalated, and every report reaches control-room dispatch within the client's SLA.",
      "For workover crews, drilling rigs, and stimulation operations moving between sites, we provide escort coverage, gate control at temporary camp locations, and personnel-safety monitoring on request."
    ],
    keywords: [
      "oil gas security Virden",
      "wellhead security Virden",
      "workover security Manitoba",
      "Virden pipeline security"
    ]
  },
  {
    slug: "event-security-winnipeg",
    serviceSlug: "event-security",
    citySlug: "winnipeg",
    heading: "Event Security. Winnipeg, Manitoba.",
    lede:
      "From the RBC Convention Centre to Canada Life Centre, from The Forks to Assiniboine Park — event security calibrated to Winnipeg's largest venues.",
    body: [
      "Winnipeg hosts more than four hundred ticketed events every year across the RBC Convention Centre, Canada Life Centre, Burton Cummings Theatre, Club Regent Event Centre, The Forks, Assiniboine Park, and dozens of secondary venues. Manitoba Security Services provides event-trained officers for concerts, corporate galas, festivals, sporting events, private receptions, and multi-day conferences across the city.",
      "Our event program covers access control at gates and back-of-house, ID verification for licensed sections in line with Manitoba's Liquor, Gaming and Cannabis Authority requirements, bag inspection, crowd management during high-density periods, and coordinated egress control during emergencies. Senior supervisors interface with venue security, event medical providers, and the Winnipeg Police Service where required.",
      "For corporate galas, private weddings, and executive receptions, we deploy plainclothes officers whose presence is felt only when needed — a discretion posture that preserves the guest experience while holding a professional security perimeter."
    ],
    keywords: [
      "event security Winnipeg",
      "concert security Winnipeg",
      "convention centre security",
      "Canada Life Centre security",
      "corporate event security Winnipeg"
    ]
  },
  {
    slug: "corporate-security-winnipeg",
    serviceSlug: "corporate-security",
    citySlug: "winnipeg",
    heading: "Corporate Security. Winnipeg, Manitoba.",
    lede:
      "Concierge-grade lobby posture for downtown office towers and executive floors across Winnipeg's corporate real estate.",
    body: [
      "Portage and Main. Broadway. St. Mary Avenue. The Exchange District. Winnipeg's corporate address book runs on lobby experiences that reflect the tenants inside — professional, controlled, welcoming, and unshakeable in a difficult moment. Manitoba Security Services staffs Winnipeg corporate lobbies with officers selected for presentation, communication, and the discipline required to hold posture across a full shift.",
      "Our corporate program manages visitor systems from paper logs to enterprise platforms, verifies contractor credentials, coordinates after-hours access on restricted floors, integrates with existing electronic access control, and escalates anomalies to facilities and HR. Officers train to the client's specific evacuation plan, fire warden hierarchy, and executive-visit protocol.",
      "For higher-profile visits — board meetings, sensitive terminations, high-value client tours — we scale to a plainclothes protective posture without disrupting the day-to-day feel of the building."
    ],
    keywords: [
      "corporate security Winnipeg",
      "office tower security Winnipeg",
      "downtown Winnipeg security",
      "concierge security Winnipeg"
    ]
  },
  {
    slug: "construction-security-brandon",
    serviceSlug: "construction-site-security",
    citySlug: "brandon",
    heading: "Construction Security. Brandon, Manitoba.",
    lede:
      "Copper, tool, and equipment protection for Brandon's active build cycle — from downtown mixed-use to greenfield industrial.",
    body: [
      "Brandon's construction pipeline is steady — commercial infill downtown, residential subdivision development, industrial expansion along the east side, and a recurring flow of institutional and infrastructure projects across the city. Manitoba Security Services builds construction security programs that fit the phase of the project and the risk profile of the trades on site.",
      "Our Brandon construction coverage runs from perimeter fencing and gate control during excavation, through overnight mobile patrols during structural work, to full manned coverage during finishing when high-value trades are stacked. Copper, tools, gas cylinders, welders, laser levels, and site-office electronics — every category is covered by a documented deterrent posture backed by GPS-verified patrol records.",
      "We coordinate with the Brandon Police Service and the RCMP where a theft investigation is opened, and provide the general contractor's site super with clean, auditable documentation for insurance and WCB purposes."
    ],
    keywords: [
      "construction security Brandon",
      "job site security Brandon",
      "copper theft prevention Brandon",
      "Brandon construction patrol"
    ]
  },
  {
    slug: "healthcare-security-winnipeg",
    serviceSlug: "healthcare-security",
    citySlug: "winnipeg",
    heading: "Healthcare Security. Winnipeg, Manitoba.",
    lede:
      "Trained officers for Winnipeg's hospital campuses, community health facilities, and long-term care environments.",
    body: [
      "A Winnipeg healthcare security posting demands more than presence. Officers protect staff and patients, control access to sensitive areas, respond to code events, de-escalate mental health incidents, and manage the emotional temperature of a waiting room during a difficult night. Manitoba Security Services trains its Winnipeg healthcare officers in verbal de-escalation, non-violent crisis intervention, and the specific protocols of the Winnipeg Regional Health Authority environment.",
      "Our healthcare officers control access to restricted areas — pharmacy, obstetrics, mental health units, ICU approaches — respond to code white, code yellow, and code silver events, and hold the strictest privacy discipline of any security posting. Documentation is precise, communication with clinical staff is clean, and escalation to the Winnipeg Police Service is handled by senior officers with proper evidence protocol.",
      "For community health centres, private clinics, and long-term care facilities across Winnipeg, we scale coverage to the operational envelope — from event-driven visits to full 24/7 posture as the client requires."
    ],
    keywords: [
      "healthcare security Winnipeg",
      "hospital security Manitoba",
      "clinic security Winnipeg",
      "long-term care security Winnipeg",
      "mental health security officer"
    ]
  },
  {
    slug: "retail-loss-prevention-winnipeg",
    serviceSlug: "retail-loss-prevention",
    citySlug: "winnipeg",
    heading: "Retail Loss Prevention. Winnipeg, Manitoba.",
    lede:
      "Uniformed deterrence and plainclothes loss prevention across Winnipeg's malls, big-box footprints, and independent retail.",
    body: [
      "Polo Park. St. Vital Centre. Kildonan Place. Grant Park. Garden City Shopping Centre. Standalone big-box footprints along Kenaston, Regent, McPhillips, and Pembina. Every Winnipeg retailer works against a shrink number, and every operational shrink program benefits from a mix of visible deterrence and structured plainclothes observation. Manitoba Security Services provides both.",
      "Our uniformed retail officers greet customers, monitor entry and exit points, respond to EAS activations, and support store staff during difficult interactions. Our plainclothes officers work under the six-step observation model — selection, concealment, continuous observation, non-payment, exit, apprehension — and document every recovery for the client's loss prevention record. Interactions with the Winnipeg Police Service are handled by trained personnel with proper evidence protocol.",
      "For mall environments, we integrate with property management, tenant retailers, and the venue's overall security program to deliver consistent standards across every tenant zone."
    ],
    keywords: [
      "retail loss prevention Winnipeg",
      "shoplifting prevention Winnipeg",
      "mall security Winnipeg",
      "big box security Winnipeg",
      "Polo Park security"
    ]
  },
  {
    slug: "warehouse-security-winnipeg",
    serviceSlug: "warehouse-industrial-security",
    citySlug: "winnipeg",
    heading: "Warehouse & Industrial Security. Winnipeg, Manitoba.",
    lede:
      "Gate control, dockyard security, and cargo integrity across CentrePort, Inkster, Symington, and Winnipeg's industrial corridors.",
    body: [
      "Winnipeg is Canada's inland port. CentrePort Canada, Inkster industrial, Symington yard, Fort Garry industrial, and the East Perimeter distribution corridors handle a substantial share of the country's east-west freight flow. Manitoba Security Services staffs Winnipeg warehouse and industrial operations with officers trained in yard control, driver check-in, seal verification, and dock supervision.",
      "Our program integrates with the client's WMS gate module or acts as the manual layer where automation is absent. Driver credentialing, live seal verification against BOL data, dock assignment coordination, and post-load walk-around inspections are handled by officers who understand the operational tempo of a live dock.",
      "For cold-storage and food-grade facilities, our officers hold hygiene training and understand the access controls that protect product integrity. For chemical and hazardous-goods warehouses, our officers are cross-trained in WHMIS, incident escalation, and emergency response coordination."
    ],
    keywords: [
      "warehouse security Winnipeg",
      "industrial security Winnipeg",
      "CentrePort security",
      "cargo security Winnipeg",
      "cold storage security Winnipeg",
      "distribution centre security"
    ]
  },
  {
    slug: "concierge-security-winnipeg",
    serviceSlug: "concierge-security",
    citySlug: "winnipeg",
    heading: "Concierge Security. Winnipeg, Manitoba.",
    lede:
      "Front-desk officers who deliver hospitality standards without compromising security posture across Winnipeg's condominium and hotel corridors.",
    body: [
      "River Heights. Osborne Village. Waterfront Drive. The Exchange District. Downtown Winnipeg's hotel corridor along Portage Avenue. Manitoba Security Services staffs Winnipeg concierge positions with officers selected for warmth, discretion, and unshakeable composure — the specific mix that carries a front desk through both a routine day and a difficult night.",
      "Our concierge officers welcome residents and guests, manage visitor logs, coordinate deliveries under a documented protocol, monitor life-safety panels, enforce building bylaws with tact, and handle difficult interactions with a de-escalation posture. Where the property runs an integrated access-control platform, we operate it. Where the property runs paper, we operate that too.",
      "For property managers with multi-building portfolios, we deliver a consistent presentation standard across every asset in the book."
    ],
    keywords: [
      "concierge security Winnipeg",
      "condo concierge Winnipeg",
      "hotel security Winnipeg",
      "hospitality security Manitoba",
      "front desk security Winnipeg"
    ]
  }
];

// Enrich with service + city references for rendering.
export const sectors = raw.map((r) => ({
  ...r,
  service: serviceBySlug(r.serviceSlug),
  city: cityBySlug(r.citySlug),
}));

export const sectorBySlug = (slug) => sectors.find((s) => s.slug === slug);
